import {FC} from 'react';
import cn from 'classnames';
import {useIsMaxSm} from 'features/auth/ui/AuthorizationSteps/useIsMaxSm';
import {
	getMyTaxLoadErrorRetryLabel,
	MY_TAX_FNS_PENDING_ACTION_LABEL,
	PARTNER_AGREEMENT_PENDING_ACTION_LABEL,
	MY_TAX_NAME_MISMATCH_CONFIRM_LABEL,
	MY_TAX_NOT_SELF_EMPLOYED_CHECK_INN_LABEL,
	REGISTRATION_COMPLETE_ACTION_LABEL,
	RegistrationButton,
	RegistrationStep,
	RegistrationWorkStatus,
} from 'features/registration';
import type {RegistrationDerived} from './useRegistrationDerived';
import styles from './RegistrationWizard.module.css';

type Props = {
	step: RegistrationStep;
	workStatus: RegistrationWorkStatus | null;
	postInnFlow: RegistrationDerived['postInnFlow'];
	isSubmitting: boolean;
	myTaxFooterLoading: 'goToSite' | 'confirm' | null;
	isRegistrationCompleteStep: boolean;
	isIdentityReviewStep: boolean;
	showResidentCodeFooter: boolean;
	canSkipResidentCode: boolean;
	residentCodeContinueVisible: boolean;
	showContinueFooter: boolean;
	continueButtonLabel: string;
	onOpenHowToBecome: () => void;
	onMyTaxIntroSecondary: () => void;
	onMyTaxIntroProceed: () => void;
	onGoToMyTax: () => void;
	onAccessGranted: () => void;
	onCompleteStatusConnection: () => void;
	onMyTaxNameMismatchConfirm: () => void;
	onMyTaxAccessErrorRetry: () => void;
	onMyTaxNotSelfEmployedCheckInn: () => void;
	onFnsPendingContinue: () => void;
	onMyTaxLoadErrorRetry: () => void;
	onMyTaxLoadErrorBack?: () => void;
	onClose: () => void;
	onResidentCodeSkip: () => void;
	onContinue: () => void;
};

/** Футеры визарда: кнопки текущего шага. */
export const RegistrationWizardFooters: FC<Props> = ({
	step,
	workStatus,
	postInnFlow,
	isSubmitting,
	myTaxFooterLoading,
	isRegistrationCompleteStep,
	isIdentityReviewStep,
	showResidentCodeFooter,
	canSkipResidentCode,
	residentCodeContinueVisible,
	showContinueFooter,
	continueButtonLabel,
	onOpenHowToBecome,
	onMyTaxIntroSecondary,
	onMyTaxIntroProceed,
	onGoToMyTax,
	onAccessGranted,
	onCompleteStatusConnection,
	onMyTaxNameMismatchConfirm,
	onMyTaxAccessErrorRetry,
	onMyTaxNotSelfEmployedCheckInn,
	onFnsPendingContinue,
	onMyTaxLoadErrorRetry,
	onMyTaxLoadErrorBack,
	onClose,
	onResidentCodeSkip,
	onContinue,
}) => {
	const isMaxSm = useIsMaxSm();
	const alfaBankFlow = Boolean(postInnFlow.intro.confirm);
	const accessPrimaryLabel = postInnFlow.access
		? isMaxSm && postInnFlow.access.primaryAction.mobileLabel
			? postInnFlow.access.primaryAction.mobileLabel
			: postInnFlow.access.primaryAction.label
		: '';

	return (
		<>
			{step === RegistrationStep.MyTaxIntro ? (
				<footer className={cn(styles.dualFooter, alfaBankFlow && styles.dualFooter_primaryFirst)}>
					{postInnFlow.intro.secondaryAction ? (
						<RegistrationButton
							variant="secondary"
							onClick={
								postInnFlow.intro.secondaryAction.kind === 'confirmInfo'
									? onMyTaxIntroSecondary
									: onOpenHowToBecome
							}
						>
							{postInnFlow.intro.secondaryAction.label}
						</RegistrationButton>
					) : null}
					<RegistrationButton
						variant="primary"
						disabled={isSubmitting}
						loading={isSubmitting}
						onClick={onMyTaxIntroProceed}
					>
						{postInnFlow.intro.proceedLabel}
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.MyTaxAccess && postInnFlow.access ? (
				<footer className={styles.dualFooter}>
					<RegistrationButton
						variant="primary"
						disabled={myTaxFooterLoading !== null}
						loading={myTaxFooterLoading === 'goToSite'}
						onClick={onGoToMyTax}
					>
						{accessPrimaryLabel}
					</RegistrationButton>
					<RegistrationButton
						variant="muted"
						disabled={myTaxFooterLoading !== null}
						loading={myTaxFooterLoading === 'confirm'}
						onClick={onAccessGranted}
					>
						{postInnFlow.access.confirmLabel}
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.MyTaxSuccess ? (
				<footer className={styles.footer}>
					<RegistrationButton
						variant="primary"
						disabled={isSubmitting}
						loading={isSubmitting}
						onClick={onCompleteStatusConnection}
					>
						Продолжить
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.MyTaxNameMismatch ? (
				<footer className={styles.footer}>
					<RegistrationButton variant="primary" onClick={onMyTaxNameMismatchConfirm}>
						{MY_TAX_NAME_MISMATCH_CONFIRM_LABEL}
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.MyTaxAccessError && postInnFlow.access ? (
				<footer className={styles.dualFooter}>
					<RegistrationButton
						variant="primary"
						disabled={myTaxFooterLoading !== null}
						loading={myTaxFooterLoading === 'goToSite'}
						onClick={onGoToMyTax}
					>
						{accessPrimaryLabel}
					</RegistrationButton>
					<RegistrationButton
						variant="secondary"
						disabled={myTaxFooterLoading !== null}
						loading={myTaxFooterLoading === 'confirm'}
						onClick={onMyTaxAccessErrorRetry}
					>
						{postInnFlow.access.confirmLabel}
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.MyTaxNotSelfEmployed ? (
				<footer className={styles.dualFooter}>
					<RegistrationButton variant="primary" onClick={onMyTaxNotSelfEmployedCheckInn}>
						{MY_TAX_NOT_SELF_EMPLOYED_CHECK_INN_LABEL}
					</RegistrationButton>
					<RegistrationButton variant="secondary" onClick={onOpenHowToBecome}>
						Как стать самозанятым
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.MyTaxFnsPending ? (
				<footer className={styles.footer}>
					<RegistrationButton variant="primary" onClick={onFnsPendingContinue}>
						{MY_TAX_FNS_PENDING_ACTION_LABEL}
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.PartnerAgreementPending ? (
				<footer className={styles.footer}>
					<RegistrationButton variant="primary" onClick={onFnsPendingContinue}>
						{PARTNER_AGREEMENT_PENDING_ACTION_LABEL}
					</RegistrationButton>
				</footer>
			) : null}

			{step === RegistrationStep.MyTaxLoadError ? (
				alfaBankFlow ? (
					<footer className={cn(styles.dualFooter, styles.dualFooter_primaryFirst)}>
						<RegistrationButton
							variant="primary"
							disabled={isSubmitting}
							loading={isSubmitting}
							onClick={onMyTaxLoadErrorRetry}
						>
							{getMyTaxLoadErrorRetryLabel(workStatus)}
						</RegistrationButton>
						<RegistrationButton variant="secondary" onClick={onMyTaxLoadErrorBack}>
							Назад
						</RegistrationButton>
					</footer>
				) : (
					<footer className={styles.footer}>
						<RegistrationButton
							variant="primary"
							disabled={isSubmitting}
							loading={isSubmitting}
							onClick={onMyTaxLoadErrorRetry}
						>
							{getMyTaxLoadErrorRetryLabel(workStatus)}
						</RegistrationButton>
					</footer>
				)
			) : null}

			{isRegistrationCompleteStep ? (
				<footer className={styles.footer}>
					<RegistrationButton variant="primary" onClick={onClose}>
						{REGISTRATION_COMPLETE_ACTION_LABEL}
					</RegistrationButton>
				</footer>
			) : null}

			{showResidentCodeFooter ? (
				<footer className={styles.dualFooter}>
					{canSkipResidentCode ? (
						<RegistrationButton variant="muted" disabled={isSubmitting} onClick={onResidentCodeSkip}>
							Пропустить
						</RegistrationButton>
					) : null}
					{residentCodeContinueVisible ? (
						<RegistrationButton
							variant="primary"
							disabled={isSubmitting}
							loading={isSubmitting}
							onClick={onContinue}
						>
							Продолжить
						</RegistrationButton>
					) : null}
				</footer>
			) : null}

			{showContinueFooter ? (
				<footer className={cn(styles.footer, isIdentityReviewStep && styles.footer_review)}>
					<RegistrationButton
						variant="primary"
						disabled={isSubmitting}
						loading={isSubmitting}
						onClick={onContinue}
					>
						{continueButtonLabel}
					</RegistrationButton>
				</footer>
			) : null}
		</>
	);
};
